/**
 * Wilder RSI aligned to a close series.
 *
 * The first reading sits at index `period` (needs `period + 1` closes). A
 * non-finite close breaks the smoothing chain — later values stay null rather
 * than resuming with a guessed seed (R2b).
 */
import { RSI_PERIOD } from '../constants/moving-average';

import { requireAveragePeriod } from './moving-average';

/**
 * What a window with NEITHER gains nor losses reads (ADR-0046 §A.2).
 *
 * `midpoint` (50) is Meridian's default and Chart Terminal's reading: no
 * directional evidence, so sit in the middle. `no-loss` (100) is the
 * reference's rule — `loss === 0` is tested before gain — and is what the
 * composite technical score binds, because ADR-0046's deviation list is CLOSED
 * and a Meridian-only 50 there would be an unenumerated deviation.
 *
 * The two agree everywhere else: zero gain with a positive loss is 0 under
 * both, and zero loss with a positive gain is 100 under both.
 */
export type FlatWindowRsi = 'midpoint' | 'no-loss';

interface WilderState {
  readonly avgGain: number;
  readonly avgLoss: number;
  readonly values: readonly (number | null)[];
  readonly broken: boolean;
}

function changeAt(current: number, previous: number | undefined): number | null {
  if (previous === undefined || !Number.isFinite(current) || !Number.isFinite(previous)) {
    return null;
  }
  return current - previous;
}

function changesOf(bars: readonly number[]): readonly (number | null)[] {
  return bars.map((current, index) => (index === 0 ? null : changeAt(current, bars[index - 1])));
}

function rsiFromAverages(
  avgGain: number,
  avgLoss: number,
  flatWindow: FlatWindowRsi,
): number | null {
  if (!Number.isFinite(avgGain) || !Number.isFinite(avgLoss)) return null;
  if (avgGain === 0 && avgLoss === 0) return flatWindow === 'no-loss' ? 100 : 50;
  if (avgLoss === 0) return 100;
  if (avgGain === 0) return 0;
  const rsi = 100 - 100 / (1 + avgGain / avgLoss);
  return Number.isFinite(rsi) ? rsi : null;
}

function isFiniteChange(change: number | null): change is number {
  return change !== null;
}

function seedAverages(
  changes: readonly (number | null)[],
  period: number,
): { readonly avgGain: number; readonly avgLoss: number } | null {
  const seed = changes.slice(1, period + 1);
  if (seed.length !== period || !seed.every(isFiniteChange)) return null;
  const gainSum = seed.reduce((sum, change) => sum + (change > 0 ? change : 0), 0);
  const lossSum = seed.reduce((sum, change) => sum + (change < 0 ? -change : 0), 0);
  return { avgGain: gainSum / period, avgLoss: lossSum / period };
}

function wilderStep(previous: number, sample: number, period: number): number {
  return (previous * (period - 1) + sample) / period;
}

interface WilderStep {
  readonly change: number | null;
  readonly period: number;
  readonly flatWindow: FlatWindowRsi;
}

function advanceWilder(state: WilderState, step: WilderStep): WilderState {
  if (state.broken || step.change === null) {
    return { ...state, values: [...state.values, null], broken: true };
  }
  const change = step.change;
  const avgGain = wilderStep(state.avgGain, change > 0 ? change : 0, step.period);
  const avgLoss = wilderStep(state.avgLoss, change < 0 ? -change : 0, step.period);
  return {
    avgGain,
    avgLoss,
    broken: false,
    values: [...state.values, rsiFromAverages(avgGain, avgLoss, step.flatWindow)],
  };
}

export function relativeStrengthIndex(
  bars: readonly number[],
  period: number = RSI_PERIOD,
  flatWindow: FlatWindowRsi = 'midpoint',
): readonly (number | null)[] {
  requireAveragePeriod(period);
  const empty = bars.map(() => null);
  if (bars.length <= period) return empty;
  const changes = changesOf(bars);
  const seed = seedAverages(changes, period);
  if (seed === null) return empty;
  const head: readonly (number | null)[] = [
    ...empty.slice(0, period),
    rsiFromAverages(seed.avgGain, seed.avgLoss, flatWindow),
  ];
  const tail = changes
    .slice(period + 1)
    .reduce<WilderState>((state, change) => advanceWilder(state, { change, period, flatWindow }), {
      avgGain: seed.avgGain,
      avgLoss: seed.avgLoss,
      values: [],
      broken: false,
    });
  return [...head, ...tail.values];
}
